import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import {
    CustomInfiniteQueryOptions,
    CustomMutationOptions,
    CustomQueryOptions,
} from '../common.types';
import { scheduleDtoToScheduleItem } from './schedule.converters';
import {
    CreateManySchedulesReqItemDto,
    SchedulesService,
    type GetSchedulesResDto,
    type CreateManySchedulesResDto,
} from '../../generated';

// Get all products lazy
interface UseSchedulesLazyKeyData {
    ids?: Array<string>;
    pageNumber?: number;
    pageSize?: number;
}

export const useSchedulesLazyKey = 'useSchedulesLazyKey';

export const useSchedulesLazy = (
    params: UseSchedulesLazyKeyData = {},
    options: CustomInfiniteQueryOptions<GetSchedulesResDto> & {
        onInvalidPagination?: () => void;
    } = {}
) => {
    const pageSize = params.pageSize || 20;

    const { data, ...other } = useInfiniteQuery({
        queryKey: [useSchedulesLazyKey, params],
        queryFn: async ({ pageParam }) => {
            const response = await SchedulesService.getSchedules({
                query: {
                    ids: params.ids,
                    page_number: pageParam?.page || 0,
                    page_size: pageSize,
                    start_at_id: pageParam?.startAtId || undefined,
                },
            });

            if (!response.data) throw new Error('Failed to get schedules');

            return response.data;
        },
        getNextPageParam: lastPage => {
            if (!lastPage.items || !lastPage.next_id) return undefined;
            return {
                startAtId: lastPage.next_id,
                page: lastPage.next_page_number,
            };
        },
        ...options,
        onError: (error: any) => {
            if (error?.status === 409) {
                options.onInvalidPagination?.();
            } else options.onError?.(error);
        },
    });

    const totalCount = data?.pages.find(page => page.total_count)?.total_count;
    const parsedData = useMemo(
        () =>
            data?.pages
                ? data.pages
                      .map(page =>
                          (page.items || []).map(scheduleDtoToScheduleItem)
                      )
                      .reduce((arr, cur) => [...arr, ...cur], [])
                : undefined,
        [data]
    );

    return {
        totalCount,
        data: parsedData,
        ...other,
    };
};

const useSchedulesKey = 'useSchedulesKey';

export const useSchedules = (
    ids: string[] = [],
    options: CustomQueryOptions<GetSchedulesResDto> = {}
) => {
    return useQuery({
        queryKey: [useSchedulesKey, ids],
        enabled: !!ids.length,
        queryFn: async () => {
            const response = await SchedulesService.getSchedules({
                query: { ids, page_number: 0, page_size: 100 },
            });

            if (!response.data) throw new Error('Failed to get schedules');

            return response.data;
        },
        ...options,
    });
};

export const useCreateSchedule = (
    options: CustomMutationOptions<
        CreateManySchedulesReqItemDto,
        CreateManySchedulesResDto
    > = {}
) => {
    return useMutation({
        mutationFn: async schedule => {
            const response = await SchedulesService.createManySchedules({
                body: { items: [schedule] },
            });

            if (!response.data) throw new Error('Failed to create schedule');
            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            // TODO invalidate schedule list
            options?.onSuccess?.(...args);
        },
    });
};
